import {
  Ng1StateDeclaration,
  Ng1ViewDeclaration,
  StateParams,
  StateRegistry,
  StateService,
} from "@uirouter/angularjs";
import { IServiceProvider } from "angular";
import {
  vsName as serverUtilityName,
  ServerUtility,
} from "../../records/server/serverUtility";
import {
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { vsName as projectCostDetailComponentName } from "../views/info";
import {
  vsComponentName as FormViewComponentName,
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import { Record } from "../../records/server/crud";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../application/UserProfile";
export const vsServiceName = "projectCostService";
export const vsProviderName = `${vsServiceName}Provider`;

const baseState = "main.project.machines";

const createState = `${baseState}.create`;
const gridState = `${baseState}.grid`;

const infoState = `${baseState}.info`;
const editState = `${infoState}.edit`;
const infoRedirectState = `${infoState}.redirect`;

export class ProjectCostServiceProvider implements IServiceProvider {
  static $inject = [];
  constructor() {}

  private readonly _projectCostTypes: IProjectCostType[] = [];
  private readonly _projectCostTabs: IProjectCostTab[] = [];

  addProjectCostType(projectCostType: IProjectCostType) {
    this._projectCostTypes.push(projectCostType);
  }

  addProjectCostTab(projectCostTab: IProjectCostTab) {
    this._projectCostTabs.push(projectCostTab);
  }

  $get = [
    "$state",
    "$stateRegistry",
    FormViewServiceName,
    serverUtilityName,
    UserProfileName,
    (
      $state: StateService,
      $stateRegistry: StateRegistry,
      formViewService: FormViewService,
      serverUtility: ServerUtility,
      userProfile: UserProfile
    ) => {
      $stateRegistry.register({
        name: baseState,
        url: "/:machine",
        abstract: true,
        params: {
          type: undefined,
        },
        resolve: {
          projectCostType: [
            vsServiceName,
            "$stateParams",
            (projectCostService: ProjectCostService, $stateParams: StateParams) =>
              projectCostService.getProjectCostType($stateParams.type),
          ],
        },
      });

      $stateRegistry.register({
        name: createState,
        url: "/create",
        component: FormViewComponentName,
        resolve: FormViewService.getResolveObjectFromFormViewConfig(
          [
            "projectCostType",
            (projectCostType: ProjectCostTypeService) =>
              formViewService.getFormViewConfig(projectCostType.formConfigName)!,
          ],
          [
            () => {
              return {};
            },
          ]
        ),
      });

      $stateRegistry.register({
        name: editState,
        url: "/edit",
        views: {
          "": {
            component: FormViewComponentName,
          },
        },
        resolve: {
          ...FormViewService.getResolveObjectFromFormViewConfig(
            [
              "projectCostType",
              (projectCostType: ProjectCostTypeService) =>
                formViewService.getFormViewConfig(projectCostType.formConfigName)!,
            ],
            ["projectCost", (projectCost: any) => projectCost],
            [
              "$state",
              ($state: StateService) => [() => $state.go("^.redirect")],
            ]
          ),
        },
      });

      $stateRegistry.register({
        name: gridState,
        url: "/grid",
        component: RecordGridViewComponentName,
        resolve: {
          ...RecordGridViewService.getResolveObjectFromGridConfiguration([
            "projectCostType",
            RecordGridViewServiceName,
            (
              projectCostType: ProjectCostTypeService,
              recordGridViewService: RecordGridViewService
            ) => {
              const gridConfiguration =
                recordGridViewService.getGridConfiguration(
                  projectCostType.gridConfigName
                );
              if (!gridConfiguration) {
                throw new Error("Couldn't resolve grid configuration.");
              }
              gridConfiguration.onAddNew = () =>
                projectCostType.createNavigate().then(() => {});
              return gridConfiguration;
            },
          ]),
          columns: [
            "projectCostType",
            "gridConfiguration",
            ColumnDefinitionServiceName,
            (
              projectCostType: ProjectCostTypeService,
              gridConfiguration: GridConfiguration,
              columnDefinitionService: ColumnDefinitionService
            ) => [
              columnDefinitionService.getLinkColDef({
                colId: "info",
                headerName: "Info",
                iconClass: "fa fa-info-circle text-primary",
                stateExpression: `${projectCostType.infoSref()}({projectCostInstanceId: data.ProjectCostInstanceId})`,
              }),
              columnDefinitionService.getLinkColDef({
                colId: "edit",
                headerName: "Edit",
                iconClass: "fa fa-pencil text-primary",
                stateExpression: `${projectCostType.editSref()}({projectCostInstanceId: data.ProjectCostInstanceId, type: data.ProjectCostInstanceType})`,
                requiresPermission: "projectCostEdit",
              }),
              ...(gridConfiguration.columns ?? []),
            ],
          ],
        },
      });

      $stateRegistry.register({
        name: infoState,
        url: "/{projectCostInstanceId:int}",
        component: projectCostDetailComponentName,
        resolve: {
          projectCost: [
            "$stateParams",
            "projectCostType",
            ($stateParams: StateParams, projectCostType: ProjectCostTypeService) =>
              projectCostType.fetchProjectCost($stateParams.projectCostInstanceId),
          ],
        },
      });

      $stateRegistry.register({
        name: infoRedirectState,
        onEnter: [
          "$state",
          "projectCostType",
          "$stateParams",
          (
            $state: StateService,
            projectCostType: ProjectCostTypeService,
            $stateParams: StateParams
          ) =>
            $state.target(projectCostType.infoSref(), {
              type: $stateParams.type,
              projectCostInstanceId: $stateParams.projectCostInstanceId,
            }),
        ],
      });

      const defaultViews = {};

      this.registerProjectCostTabs(
        $stateRegistry,
        infoState,
        this._projectCostTabs,
        defaultViews
      );

      const projectCostTypes: ProjectCostTypeService[] = [];
      const projectCostService = new ProjectCostService(
        $state,
        projectCostTypes,
        this._projectCostTabs,
        formViewService,
        serverUtility,
        userProfile
      );
      projectCostTypes.push(
        ...this._projectCostTypes.map((ct) => {
          const typeSpecificTabs = (ct.tabs ??= []);
          this.registerProjectCostTabs(
            $stateRegistry,
            infoState,
            typeSpecificTabs,
            defaultViews
          );
          ct.tabs = [...typeSpecificTabs, ...this._projectCostTabs];
          return new ProjectCostTypeService(projectCostService, ct);
        })
      );

      return projectCostService;

    },
  ];

  registerProjectCostTabs(
    $stateRegistry: StateRegistry,
    basePath: string,
    tabs: IProjectCostTab[],
    views?: { [key: string]: Ng1ViewDeclaration }
  ) {
    tabs.forEach((t) => {
      const stateName = (t.state.name ??= `${basePath}.${t.uiSref}`);
      t.state.views = { ...views, ...t.state.views };

      $stateRegistry.register(t.state);
      this.registerProjectCostTabs($stateRegistry, stateName, t.children ?? []);
    });
  }
}

export class ProjectCostService {
  constructor(
    private readonly $state: StateService,
    private readonly projectCostTypes: ProjectCostTypeService[],
    private readonly projectCostTabs: IProjectCostTab[],
    private readonly formViewService: FormViewService,
    private readonly serverUtility: ServerUtility,
    private readonly userProfile: UserProfile
  ) {}

  getProjectCostType(id: string): ProjectCostTypeService | undefined {
    return this.projectCostTypes.find((ct) => ct.id === id);
  }

  getProjectCostTabs(): IProjectCostTab[] {
    return this.projectCostTabs;
  }

  getProjectCostTypes(): ProjectCostTypeService[] {
    return this.projectCostTypes;
  }

  gridNavigate(type: string) {
    return this.$state.go(gridState, { type });
  }

  gridSref(type: string) {
    return gridState;
  }

  infoNavigate(type: string, projectCostInstanceId: number) {
    return this.$state.go(
      this.infoSref(type),
      { type, projectCostInstanceId },
      { reload: true }
    );
  }

  infoSref(type: string, tab?: IProjectCostTab, routeParams?: any) {
    const projectCostType = this.getProjectCostType(type);
    tab ??= projectCostType?.tabs?.at(0);

    const paramString = routeParams ? `(${JSON.stringify(routeParams)})` : "";

    if (tab) {
      return `${infoState}.${tab.uiSref}${paramString}`;
    }

    return infoState;
  }

  editNavigate(type: string, projectCostInstanceId: number) {
    return this.$state.go(this.editSref(type), { type, projectCostInstanceId });
  }

  editSref(type: string) {
    return editState;
  }

  createNavigate(type: string) {
    return this.$state.go(this.createSref(type), { type });
  }

  createSref(type: string) {
    return createState;
  }

  hasSensitiveInfoPermission(type: string) {
    const projectCostType = this.getProjectCostType(type);
    const permission = projectCostType?.sensitiveInfoPermission;
    return permission ? this.userProfile.hasPermission(permission) : false;
  }

  fetchProjectCost<TProjectCost extends Record>(
    type: string,
    projectCostInstanceId: number
  ) {
    const projectCostType = this.getProjectCostType(type)!;

    return this.serverUtility.getById<TProjectCost>({
      url: `/api/project/${projectCostType.id}/byInstance`,
      id: projectCostInstanceId,
    });
  }

  submitProjectCost<TProjectCost extends Record & IHasProjectCostInstanceId>(
    type: string,
    projectCost: TProjectCost,
    formViewController: FormViewController
  ) {
    if (!projectCost) {
      throw new Error("ProjectCost object is undefined.");
    }

    const addressBookType = this.getProjectCostType(type)!;

    const newRecord = !projectCost.Id;
    const apiEndpoint =
      `/api/addressBook/${addressBookType.id}` + (newRecord ? "" : `/${projectCost.Id}`);

    return this.formViewService.submitForm<TProjectCost>({
      formObject: projectCost,
      formViewController,
      method: newRecord ? "POST" : "PUT",
      url: apiEndpoint,
    });
  }

  private getFirstInfoState(projectCostType: IProjectCostType) {
    return projectCostType.tabs?.at(0)?.uiSref ?? infoState;
  }
}

//do this so projectCost service is instantiated and routes get registered as early as possible
ProjectCostServiceRun.$inject = [vsServiceName];
export function ProjectCostServiceRun() {}

export class ProjectCostTypeService implements IProjectCostType {
  constructor(
    private readonly projectCostService: ProjectCostService,
    projectCostType: IProjectCostType
  ) {
    Object.assign(this, projectCostType);

    this.formConfigName ??= projectCostType.name;
    this.gridConfigName ??= projectCostType.name;
    this.tabs ??= [];
  }

  fetchProjectCost(projectCostInstanceId: number) {
    return this.projectCostService.fetchProjectCost(this.id, projectCostInstanceId);
  }

  submitProjectCost(
    projectCost: Record & IHasProjectCostInstanceId,
    formViewController: FormViewController
  ) {
    return this.projectCostService.submitProjectCost(
      this.id,
      projectCost,
      formViewController
    );
  }

  infoSref(tab?: IProjectCostTab, routeParams?: any) {
    return this.projectCostService.infoSref(this.id, tab, routeParams);
  }

  infoNavigate(projectCostInstanceId: number) {
    return this.projectCostService.infoNavigate(this.id, projectCostInstanceId);
  }

  gridSref() {
    this.projectCostService.gridSref(this.id);
  }

  gridNavigate() {
    return this.projectCostService.gridNavigate(this.id);
  }

  editNavigate(projectCostInstanceId: number) {
    return this.projectCostService.editNavigate(this.id, projectCostInstanceId);
  }

  editSref() {
    return this.projectCostService.editSref(this.id);
  }

  createNavigate() {
    return this.projectCostService.createNavigate(this.id);
  }

  createSref() {
    return this.projectCostService.createSref(this.id);
  }

  hasSensitiveInfoPermission(): boolean {
    return this.projectCostService.hasSensitiveInfoPermission(this.id);
  }
  sensitiveInfoPermission?: string;

  id!: string;
  name!: string;
  formConfigName: string;
  gridConfigName: string;
  detailComponentName!: string;
  tabs?: IProjectCostTab[];
}

export interface IProjectCostType {
  id: string;
  name: string;
  //formComponentName: string;
  detailComponentName: string;
  formConfigName: string;
  gridConfigName: string;
  tabs?: IProjectCostTab[];
  sensitiveInfoPermission?: string | string[];
}

export interface IHasProjectCostInstanceId {
  ProjectCostInstanceId: number;
}

export interface IProjectCostTab {
  uiSref: string;
  name: string;
  state: Ng1StateDeclaration;
  children?: IProjectCostTab[];
}
